/**
 * MovementRow Component
 *
 * Displays an accounting movement (prima nota) in row format using BaseRow layout.
 * Shows account, counterparty, amount, direction, and reconciliation status.
 */

import React, { useMemo, useState } from 'react';
import { BookOpen, TrendingUp, TrendingDown, CheckCircle2 } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { Movement } from '@business-assistant/shared/domain';
import type { ActionType } from '../../types/row.types';

// =============================================================================
// TYPES
// =============================================================================

export interface MovementRowProps {
  entity: Movement;
  /** Account name for display */
  accountName?: string;
  onTitleClick?: (movement: Movement) => void;
  onActionClick?: (actionType: string, movementId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MovementRow({
  entity: movement,
  accountName,
  onTitleClick,
  onActionClick,
  onAvatarClick
}: MovementRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware date formatting
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Primary action state (seen/acknowledged)
  const [isSeen, setIsSeen] = useState(movement.reconciled);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(movement.id);
  }, [movement.id]);

  // Left slot - Primary Action + Company Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="eye"
        active={isSeen}
        onChange={(seen) => {
          setIsSeen(seen);
          onActionClick?.('seen', movement.id);
        }}
      />
      <EntityAvatar
        items={{ name: movement.counterparty }}
        type="company"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get available actions for movements
  const availableActions = useMemo(() => {
    const defaultActions: ActionType[] = ['note', 'file', 'invoice'];
    try {
      const registryActions = getAvailableActions(movement.id);
      return registryActions.length > 0 ? registryActions : defaultActions;
    } catch {
      return defaultActions;
    }
  }, [movement.id]);

  // Build title from counterparty
  const title = movement.counterparty;

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Amount with direction
    const amount = (movement.amount / 100).toFixed(2);
    parts.push(
      <span
        key="amount"
        style={{
          ...styles.amount,
          color: movement.direction === 'in' ? '#16a34a' : '#dc2626',
        }}
      >
        {movement.direction === 'in' ? '+' : '-'}€{amount}
      </span>
    );

    // Account name
    if (accountName) {
      parts.push(
        <span key="account" style={styles.account}>
          {accountName}
        </span>
      );
    }

    // Reconciled indicator
    if (movement.reconciled) {
      parts.push(
        <span key="reconciled" style={styles.reconciledBadge}>
          <CheckCircle2 size={10} style={{ marginRight: '2px' }} />
          {t('movements.reconciled')}
        </span>
      );
    }

    // Linked to invoice
    if (movement.invoiceId) {
      parts.push(
        <span key="invoice" style={styles.invoiceBadge}>
          {t('movements.linkedInvoice')}
        </span>
      );
    }

    // Memo
    if (movement.memo) {
      const memoPreview = movement.memo.length > 30
        ? movement.memo.substring(0, 30) + '...'
        : movement.memo;
      parts.push(
        <span key="memo" style={styles.memo}>
          {memoPreview}
        </span>
      );
    }

    // Date
    parts.push(
      <span key="date" style={styles.date}>
        {formatDate(movement.at)}
      </span>
    );

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [movement, accountName, t, formatDate]);

  // Icon based on direction
  const icon = useMemo(() => {
    if (movement.direction === 'in') {
      return <TrendingUp size={16} style={{ color: '#16a34a' }} />;
    } else {
      return <TrendingDown size={16} style={{ color: '#dc2626' }} />;
    }
  }, [movement.direction]);

  return (
    <BaseRow
      entity={{ ...movement, title }}
      entityType="movement"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isSeen || movement.reconciled}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// formatDate is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  amount: {
    fontWeight: 600,
    fontSize: '13px',
  } as React.CSSProperties,
  account: {
    background: '#f3f4f6',
    color: '#374151',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
  } as React.CSSProperties,
  reconciledBadge: {
    display: 'flex',
    alignItems: 'center',
    background: '#dcfce7',
    color: '#16a34a',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 500,
  } as React.CSSProperties,
  invoiceBadge: {
    background: '#dbeafe',
    color: '#1e40af',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 500,
  } as React.CSSProperties,
  memo: {
    color: '#666',
    fontStyle: 'italic',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  date: {
    color: '#999',
  } as React.CSSProperties,
};
